import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Card } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import api, { formatINR } from "@/lib/api";
import StatusBadge from "@/components/StatusBadge";
import SourceBadge from "@/components/SourceBadge";
import { useAuth, roleHomePath } from "@/contexts/AuthContext";

const STATUS_OPTIONS = [
  "All", "Lead Created", "Documents Pending", "CarDr Report Pending",
  "Valuation Pending", "Underwriting Pending", "Additional Documents Required",
  "Approved", "Approved with Conditions", "Rejected", "Sanction Letter Generated", "Disbursed",
];

export default function ApplicationsList({ scope }) {
  const { user } = useAuth();
  const [items, setItems] = useState([]);
  const [q, setQ] = useState("");
  const [status, setStatus] = useState("All");
  const [sourceFilter, setSourceFilter] = useState("All");
  const [cityFilter, setCityFilter] = useState("All");
  const [loading, setLoading] = useState(true);

  const load = async () => {
    setLoading(true);
    const params = {};
    if (status && status !== "All") params.status = status;
    if (q) params.q = q;
    const { data } = await api.get("/applications", { params });
    setItems(data);
    setLoading(false);
  };

  useEffect(() => { load(); /* eslint-disable-next-line */ }, [status]);

  const filtered = items.filter(a => {
    if (sourceFilter !== "All" && (a.source || "").toLowerCase() !== sourceFilter.toLowerCase()) return false;
    if (cityFilter !== "All" && (a.borrower?.city || "") !== cityFilter) return false;
    return true;
  });
  const cities = Array.from(new Set(items.map(a => a.borrower?.city).filter(Boolean))).sort();

  const basePath = roleHomePath(user?.role);
  return (
    <div data-testid="applications-list">
      <div className="flex flex-wrap items-center justify-between gap-3 mb-5">
        <div>
          <h1 className="text-2xl font-semibold text-slate-900">Applications</h1>
          <p className="text-sm text-slate-600 mt-1">{filtered.length} of {items.length} result{items.length === 1 ? "" : "s"}</p>
        </div>
        <div className="flex flex-wrap gap-3 items-center">
          <form onSubmit={e => { e.preventDefault(); load(); }}>
            <Input data-testid="apps-search-input" placeholder="ID, name, PAN, reg no" value={q} onChange={e => setQ(e.target.value)} className="w-56" />
          </form>
          <Select value={status} onValueChange={setStatus}>
            <SelectTrigger className="w-48" data-testid="apps-status-filter"><SelectValue placeholder="Status" /></SelectTrigger>
            <SelectContent>
              {STATUS_OPTIONS.map(s => <SelectItem key={s} value={s}>{s}</SelectItem>)}
            </SelectContent>
          </Select>
          <Select value={sourceFilter} onValueChange={setSourceFilter}>
            <SelectTrigger className="w-36" data-testid="apps-source-filter"><SelectValue placeholder="Source" /></SelectTrigger>
            <SelectContent>
              <SelectItem value="All">All sources</SelectItem>
              <SelectItem value="borrower">Borrower</SelectItem>
              <SelectItem value="dealer">Dealer</SelectItem>
              <SelectItem value="dsa">DSA</SelectItem>
              <SelectItem value="admin">Admin</SelectItem>
            </SelectContent>
          </Select>
          <Select value={cityFilter} onValueChange={setCityFilter}>
            <SelectTrigger className="w-40" data-testid="apps-city-filter"><SelectValue placeholder="City" /></SelectTrigger>
            <SelectContent>
              <SelectItem value="All">All cities</SelectItem>
              {cities.map(c => <SelectItem key={c} value={c}>{c}</SelectItem>)}
            </SelectContent>
          </Select>
        </div>
      </div>

      <Card className="border-slate-200 overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-slate-50 text-slate-500 text-xs uppercase tracking-wider">
              <tr>
                <th className="text-left px-4 py-3">App ID</th>
                <th className="text-left px-4 py-3">Borrower</th>
                <th className="text-left px-4 py-3">Vehicle</th>
                <th className="text-left px-4 py-3">Loan</th>
                <th className="text-left px-4 py-3">Status</th>
                <th className="text-left px-4 py-3">Source</th>
              </tr>
            </thead>
            <tbody>
              {loading ? (
                <tr><td colSpan={6} className="p-6 text-center text-slate-500">Loading…</td></tr>
              ) : filtered.length === 0 ? (
                <tr><td colSpan={6} className="p-6 text-center text-slate-500">No applications match your filters.</td></tr>
              ) : filtered.map(a => (
                <tr key={a.id} className="border-t border-slate-100 hover:bg-slate-50" data-testid={`apps-row-${a.application_id}`}>
                  <td className="px-4 py-3">
                    <Link to={`${basePath}/applications/${a.id}`} className="font-mono text-teal-700 font-medium" data-testid={`apps-link-${a.application_id}`}>{a.application_id}</Link>
                  </td>
                  <td className="px-4 py-3">
                    <div className="font-medium text-slate-900">{a.borrower?.full_name || "—"}</div>
                    <div className="text-xs text-slate-500">{a.borrower?.city || ""}</div>
                  </td>
                  <td className="px-4 py-3 text-slate-700">{a.vehicle?.make} {a.vehicle?.model} · {a.vehicle?.year || ""}</td>
                  <td className="px-4 py-3 font-mono text-slate-700">{formatINR(a.loan_offer?.loan_amount || a.loan?.desired_loan_amount)}</td>
                  <td className="px-4 py-3"><StatusBadge status={a.status} /></td>
                  <td className="px-4 py-3"><SourceBadge source={a.source} /></td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </Card>
    </div>
  );
}
